import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntdLayout, Button } from 'antd';
import { 
	MessageOutlined,
	MenuFoldOutlined,
	MenuUnfoldOutlined,
	ToolOutlined,
} from '@ant-design/icons';
import { Header } from './header/indexHeader';
import { useState, useEffect, useRef } from 'react';
import ChatHistory from '@/components/ChatHistory';
import './layout.less';
// import { useValidateLogin } from '@/hooks/hooksIndex';
import { CustomMenu } from '@/components/Menu';
import classNames from 'classnames';
import { useLayoutStore } from '@/store/layoutStore';
import useKeyboardHeight from '@/hooks/useKeyboardHeight';
const { Sider, Content } = AntdLayout;

export const Layout = () => {
	const navigate = useNavigate();
	const location = useLocation();
	// const [collapsed, setCollapsed] = useState(true);
	const { collapsed, setCollapsed } = useLayoutStore();
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
	
	// 使用键盘高度钩子
	const { keyboardHeight, isKeyboardVisible, isIOS } = useKeyboardHeight();

	// 验证用户登录状态
	// useValidateLogin();

	// 添加触摸相关的状态
	const touchStartXRef = useRef<number>(0);
	const touchEndXRef = useRef<number>(0);
	const minSwipeDistance = 80; // 最小滑动距离

	// 监听窗口大小变化
	useEffect(() => {
		const handleResize = () => {
			const width = window.innerWidth;
			setIsMobile(width <= 768);
			// 在移动端自动折叠侧边栏
			if (width <= 768) {
				setCollapsed(true);
			}
		};

		window.addEventListener('resize', handleResize);
		handleResize(); // 初始化

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// 处理触摸结束
	const handleTouchEnd = (e: TouchEvent) => {
		// 检查触摸是否来自历史记录列表或agent卡片容器
		const isHistoryTouch = (e.target as HTMLElement).closest('.chat-history-list');
		const isAgentCardsTouch = (e.target as HTMLElement).closest('.agent-cards-container');
		
		if (isHistoryTouch || isAgentCardsTouch) {
			return;
		}

		touchEndXRef.current = e.changedTouches[0].clientX;
		const swipeDistance = touchEndXRef.current - touchStartXRef.current;

		if (isMobile) {
			// 向右滑动且菜单是收起状态
			if (swipeDistance > minSwipeDistance && collapsed) {
				setCollapsed(false); // 展开菜单
			// 向左滑动且菜单是展开状态
			} else if (swipeDistance < -minSwipeDistance && !collapsed) {
				setCollapsed(true);
				// 判断 > 10 是因为防止误触
			} else if ((Math.abs(swipeDistance) > 25) && (Math.abs(swipeDistance) < minSwipeDistance) && !collapsed) {
				// 向左滑动且菜单是展开状态，但滑动距离小于最小滑动距离，则收起菜单
				setCollapsed(true);
			}
		}
	};

	// 处理触摸开始
	const handleTouchStart = (e: TouchEvent) => {
		// 检查触摸是否来自agent卡片容器
		const isAgentCardsTouch = (e.target as HTMLElement).closest('.agent-cards-container');
		if (isAgentCardsTouch) {
			return;
		}
		
		touchStartXRef.current = e.touches[0].clientX;
	};

	// 添加触摸事件监听
	useEffect(() => {
		document.body.addEventListener('touchstart', handleTouchStart);
		document.body.addEventListener('touchend', handleTouchEnd);

		return () => {
			document.body.removeEventListener('touchstart', handleTouchStart);
			document.body.removeEventListener('touchend', handleTouchEnd);
		};
	}, [isMobile, collapsed]);

	const menuItems = [
		{
			key: '/chat',
			icon: <MessageOutlined />,
			label: '问答',
		},
		// {
		// 	key: '/creativity',
		// 	icon: <VideoCameraOutlined />,
		// 	label: '创作',
		// },
		// {
		// 	key: '/learn',
		// 	icon: <ReadOutlined />,
		// 	label: '学习',
		// },
		{
			key: '/tools',
			icon: <ToolOutlined />,
			label: '工具',
		},
	];

	const handleContentClick = (e: React.MouseEvent) => {
		// 检查点击是否来自历史记录列表
		const isHistoryClick = (e.target as HTMLElement).closest('.chat-history-list');
		if (!isHistoryClick && isMobile && !collapsed) {
			setCollapsed(true);
		}
	};

	const handleToggleCollapse = (e: React.MouseEvent | React.TouchEvent) => {
		// 阻止默认行为和事件冒泡
		e.preventDefault();
		e.stopPropagation();
		
		// 添加延迟以确保事件处理正确
		setTimeout(() => {
			setCollapsed(!collapsed);
		}, 0);
	};

	// 添加iOS键盘相关样式到内容区域
	const contentStyle: React.CSSProperties = {
		touchAction: 'auto' as const,     // 修改这里，允许所有触摸行为
		position: 'relative',
		zIndex: collapsed ? 'auto' : 1000,
		height: isKeyboardVisible ? `calc(100% - ${keyboardHeight}px)` : '100%',
		// backgroundColor: '#f5f5f5',
		backgroundColor: '#fff',
		borderRadius: isMobile ? '0' : '16px 16px 0 0',
		overflowY: 'auto' as const,
		WebkitOverflowScrolling: 'touch' as const,
		paddingBottom: isKeyboardVisible ? `${keyboardHeight}px` : (isIOS ? 'env(safe-area-inset-bottom, 0px)' : '0'),
		paddingTop: isIOS ? 'env(safe-area-inset-top, 0px)' : '0',
	};
	
	// 添加iOS键盘相关样式到侧边栏切换按钮
	const toggleButtonStyle = {
		position: isMobile ? 'fixed' : 'absolute',
		touchAction: 'none',
		marginTop: '6px',
		...(isMobile ? {
			left: collapsed ? '20px' : '150px',
			transform: 'translateX(-50%)',
			top: '56px',
			// background: 'var(--theme-bg)',
			color: 'rgba(28, 28, 28, 0.9)',
			marginLeft: '2px',
			borderRadius: '4px',
			width: '24px',
			height: '24px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			transition: 'left 0.2s',
			cursor: 'pointer',     // 添加鼠标指针样式
			outline: 'none',
			border: 'none',
		} : {
			left: '50%',
			top: 0,
			transform: 'translateX(-50%)',
			background: 'transparent',
			borderRadius: '0 0 4px 4px',
			marginLeft: '4px',
		}),
		zIndex: 999,
		outline: 'none',
		bottom: isKeyboardVisible 
			? `calc(20px + ${keyboardHeight}px + env(safe-area-inset-bottom, 0px))` 
			: (isIOS ? 'calc(20px + env(safe-area-inset-bottom, 0px))' : '20px'),
		transition: 'left 0.3s cubic-bezier(0.2, 0, 0, 1), bottom 0.3s ease',
	};

	return (
		<AntdLayout className={classNames('layout-container', {
			'keyboard-visible': isKeyboardVisible,
			'ios-device': isIOS
		})}>
			<Header />
			<AntdLayout style={{ 
				height: `calc(100vh - var(--header-height))`,
				transition: 'all 0.3s ease',
				background: 'var(--theme-bg)'
			}}>
				<Sider
					width={300}
					collapsible
					collapsed={collapsed}
					onCollapse={setCollapsed}
					breakpoint="lg"
					collapsedWidth={isMobile ? 0 : 60}
					trigger={null}
					className={classNames({
						'keyboard-visible-sider': isKeyboardVisible,
						'ios-sider': isIOS
					})}
					style={{
						background: 'transparent',
						position: 'relative',
					}}
				>
					<div style={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%'  // 确保flex容器占满Sider高度
					}}>
						<Button
							type="text"
							icon={collapsed ? <MenuUnfoldOutlined style={{ fontSize: '18px' }} /> : <MenuFoldOutlined style={{ fontSize: '18px' }} />}
							onClick={handleToggleCollapse}
							onTouchStart={(e) => {
								// e.preventDefault();
								e.stopPropagation();
							}}
							onTouchEnd={handleToggleCollapse}
							style={toggleButtonStyle as React.CSSProperties}
						/>
						<CustomMenu
							items={menuItems}
							onClick={(key) => {
								// 优化聊天相关路由处理逻辑
								if (key === '/chat') {
									// 如果当前已经在聊天列表页，不做任何操作
									if (location.pathname === '/chat') {
										return;
									}
									
									// 如果在聊天详情页，导航到聊天列表页
									if (location.pathname.startsWith('/chat/')) {
										navigate('/chat');
										return;
									}
									
									// 其他情况，导航到聊天列表页
									navigate(key);
									return;
								}
								
								// 编辑页面pathname包含key，则不进行跳转
								if (location.pathname?.includes(key)) {
									return;
								}
								
								// 其他页面正常导航
								console.log('Navigating to:', key);
								navigate(key);
							}}
							className={classNames({
								'menu-collapsed': collapsed,
								'keyboard-visible-menu': isKeyboardVisible,
								'ios-menu': isIOS
							})}
							style={{ flex: '0 0 auto' }}  // 菜单不伸缩
						/>
						{/* <div 
							className="menu-history-container" 
							style={{ 
								flex: '1 1 auto',  // 占用所有剩余空间
								overflowY: 'auto',  // 内容过多时显示滚动条
								minHeight: 0  // 允许元素在 flex 容器中收缩
							}}
						>
							{location.pathname === '/chat' && !collapsed && <ChatHistory />}
						</div> */}
						{location.pathname.includes('/chat') && (
							<div 
								className="menu-history-container" 
								style={{ 
									display: collapsed ? 'none' : 'block',
									flex: '1 1 auto',
									overflowY: 'auto',
									minHeight: 0
								}}
							>
								<ChatHistory />
							</div>
						)}
					</div>
				</Sider>
				<Content
					className={classNames({
						'content-container': true,
						'keyboard-visible-content': isKeyboardVisible,
						'sidebar-open': isMobile && !collapsed, // 毛玻璃效果类名
						'menu-open': isMobile && !collapsed, // 菜单打开时的遮罩效果类名
						'blur-effect-medium': isMobile && !collapsed, // 添加中等模糊效果类名
						'ios-content': isIOS,
					})}
					style={contentStyle}
					onClick={handleContentClick}
				>
					<Outlet />
				</Content>
			</AntdLayout>
		</AntdLayout>
	);
};
